import { create } from 'zustand';

import { postReferralAction } from './referrals-api';

import type { Customer } from '@/features/intelligence-dashboard/customer.schema';
import type { Product } from '@/features/product-scan/product-scan.schema';

/**
 * Client-side referral store (SCRUM-325) — Phase 1 of the referral suite.
 *
 * Mirrors the data model of Ryan's v6 prototype (`REFERRALS` seed array +
 * handlers in app-shell 6.html): status vocabulary pending → reviewed →
 * campaign_sent / denied, optional explicit assignee vs. default
 * Branch-Manager routing, an events audit trail, and teller unread flags.
 *
 * Phase 2a (SCRUM-305/342/343) swaps the ACTION INTERNALS to the real API
 * (POST /api/referrals, GET /api/referrals, GET /api/users) — the component
 * contracts (state shape + action signatures) are designed to survive that
 * swap unchanged. Workflow actions persist via the SCRUM-341 workflow endpoints
 * (fire-and-forget with local-first mutation).
 */

export type ReferralStatus = 'pending' | 'reviewed' | 'campaign_sent' | 'denied';

export interface ReferralAssignee {
  name: string;
  email: string;
  role: string;
  /** Server user id (from GET /api/users) — set for API-sourced teammates (SCRUM-343). */
  userId?: string;
}

export interface ReferralEvent {
  type: 'reassigned' | 'released' | 'denied' | 'converted';
  /** Recipient of the action, when applicable (reassigned/released → owner name). */
  to?: string;
  /** Display name of the actor who performed the action. */
  by: string;
  note?: string;
  daysAgo: number;
}

export interface Referral {
  id: string;
  /** Live customer id when resolvable; '' when live data was unavailable. */
  customerId: string;
  customerName: string;
  /** Live product id when resolvable; '' when live data was unavailable. */
  productId: string;
  productName: string;
  /** Display name of the submitting frontline employee. */
  tellerName: string;
  /** Branch driving the default Branch-Manager routing. */
  branch: string;
  note: string;
  submittedDaysAgo: number;
  status: ReferralStatus;
  /** Explicit personal assignment; null = default Branch-Manager queue. */
  assignee: ReferralAssignee | null;
  events: ReferralEvent[];
  /** Unread flag for the submitting teller (set by every routed action). */
  tellerUnread: boolean;
  /** Set when the nightly ingestion detects the customer acquired the product. */
  converted: boolean;
  denialNote?: string;
}

/** Display metadata per status — labels + tint keys per the v6 prototype. */
export const REFERRAL_STATUS_META: Record<
  ReferralStatus,
  { label: string; tint: 'pending' | 'reviewed' | 'success' | 'denied' }
> = {
  pending: { label: 'New', tint: 'pending' },
  reviewed: { label: 'Under review', tint: 'reviewed' },
  campaign_sent: { label: 'Campaign sent', tint: 'success' },
  denied: { label: 'Denied', tint: 'denied' },
};

/**
 * The demo teammate roster — the v6 prototype's `gatherAllUsers()` people
 * (dedup by email). Phase 2a replaces this with GET /api/users (the same
 * people are seeded server-side by SCRUM-308).
 */
export const TEAMMATES: readonly ReferralAssignee[] = [
  {
    name: 'Carol Whitfield',
    email: 'carol.whitfield@demo.bankiq',
    role: 'Chief Operating Officer',
  },
  { name: 'David Lin', email: 'david.lin@demo.bankiq', role: 'VP Customer Experience' },
  { name: 'Reggie Foxworth', email: 'reggie.foxworth@demo.bankiq', role: 'EVP Retail Banking' },
  { name: 'Priya Desai', email: 'priya.desai@demo.bankiq', role: 'Relationship Manager' },
  { name: 'Tom Reinholt', email: 'tom.reinholt@demo.bankiq', role: 'Commercial Banker' },
  { name: 'Janelle Cortés', email: 'janelle.cortes@demo.bankiq', role: 'Personal Banker' },
  { name: 'Daniel Kovacs', email: 'daniel.kovacs@demo.bankiq', role: 'Wealth Advisor' },
  { name: 'Marcus Hale', email: 'marcus.hale@demo.bankiq', role: 'Branch Manager' },
  { name: 'Anika Bauer', email: 'anika.bauer@demo.bankiq', role: 'Branch Manager' },
  { name: 'Lucia Vargas', email: 'lucia.vargas@demo.bankiq', role: 'Senior Teller' },
  { name: 'Sam Patel', email: 'sam.patel@demo.bankiq', role: 'Teller' },
];

const BRANCH_MANAGERS: readonly ReferralAssignee[] = TEAMMATES.filter(
  (t) => t.role === 'Branch Manager',
);

/**
 * Default owner for an unassigned referral: the branch's manager. Our live
 * customer data carries real branch names (not the prototype's fixed three),
 * so the demo maps each branch deterministically onto one of the two seeded
 * Branch Managers. Replaced by real branch assignment with SCRUM-290.
 */
export function defaultOwnerFor(branch: string): ReferralAssignee & { isDefault: true } {
  let h = 0;
  for (let i = 0; i < branch.length; i++) h = (h * 31 + branch.charCodeAt(i)) | 0;
  const bm = BRANCH_MANAGERS[Math.abs(h) % BRANCH_MANAGERS.length] ?? BRANCH_MANAGERS[0];
  return {
    name: bm?.name ?? 'Branch Manager',
    email: bm?.email ?? 'branch.manager@demo.bankiq',
    role: 'Branch Manager',
    isDefault: true,
  };
}

/** Effective owner: the explicit assignee, else the branch's default manager. */
export function effectiveOwner(r: Referral): ReferralAssignee & { isDefault?: boolean } {
  return r.assignee ?? defaultOwnerFor(r.branch);
}

/** Whether a referral sits in the given user's bucket (v6 `referralIsMine`). */
export function isMine(r: Referral, email: string | undefined): boolean {
  if (!email) return false;
  return effectiveOwner(r).email.toLowerCase() === email.toLowerCase();
}

/** "Today" / "Yesterday" / "Nd ago" per the v6 prototype. */
export function timeAgoLabel(daysAgo: number): string {
  if (daysAgo <= 0) return 'Today';
  if (daysAgo === 1) return 'Yesterday';
  return `${String(daysAgo)}d ago`;
}

/**
 * Prototype seed rows (v6 `REFERRALS`), minus customer/product identity —
 * those are resolved against LIVE data at seed time (plan audit finding #1:
 * prototype ids don't exist in our database, and the drawer/deep-links need
 * real customer records).
 */
const SEED_TEMPLATE = [
  {
    productName: 'IRA',
    tellerName: 'Lucia Vargas',
    // SCRUM-843: this note used to say "Asked about CDs at the counter today" on an
    // IRA referral, so the teller's note contradicted the product on the customer's
    // own profile. The note now matches the product it was filed against.
    note: 'Asked about retirement savings options at the counter today. Wants info on IRA rates.',
    submittedDaysAgo: 1,
    status: 'pending' as ReferralStatus,
    assignee: null,
    converted: false,
  },
  {
    productName: 'Choice Money Market',
    tellerName: 'Lucia Vargas',
    note: 'Mentioned moving funds to a higher-yield account.',
    submittedDaysAgo: 2,
    status: 'reviewed' as ReferralStatus,
    assignee: { name: 'Priya Desai', email: 'priya.desai@demo.bankiq', role: 'Bank Officer' },
    converted: false,
  },
  {
    productName: 'Small Business Checking',
    tellerName: 'Sam Patel',
    note: 'Small business with growing deposits. Worth a follow-up.',
    submittedDaysAgo: 3,
    status: 'campaign_sent' as ReferralStatus,
    assignee: { name: 'Tom Reinholt', email: 'tom.reinholt@demo.bankiq', role: 'Bank Officer' },
    converted: true,
  },
  {
    productName: 'Smart Savings',
    tellerName: 'Lucia Vargas',
    note: '',
    submittedDaysAgo: 5,
    status: 'denied' as ReferralStatus,
    assignee: null,
    converted: false,
  },
  {
    productName: 'Regular Savings 50 Plus',
    tellerName: 'Sam Patel',
    note: 'Long-time customers nearing retirement.',
    submittedDaysAgo: 6,
    status: 'pending' as ReferralStatus,
    assignee: null,
    converted: false,
  },
];

/** Deterministic customer picks for the 5 seeds (stable across sessions). */
const SEED_CUSTOMER_INDICES = [3, 7, 11, 15, 19];

/** Fallback display names (from the v6 seeds) when live data is unavailable. */
const SEED_FALLBACK_NAMES = [
  'Bradford, Heather',
  'Barnett, Christopher',
  'Gonzalez & Co.',
  'Petersen, Jerry',
  'Thompson, Aaron',
];

export interface SubmitReferralInput {
  customerId: string;
  customerName: string;
  productId: string;
  productName: string;
  branch: string;
  note: string;
  assignee: ReferralAssignee | null;
  /** Display name of the submitting user (from the auth store). */
  submittedBy: string;
  /** Signed-in email — the demo-era referrer identity for POST /api/referrals (SCRUM-343). */
  submitterEmail?: string;
}

interface ReferralsState {
  referrals: Referral[];
  seeded: boolean;
  /**
   * Idempotently seed the demo referrals, resolving customers by
   * deterministic index and products by name against the live datasets.
   * Falls back to prototype display names (with empty ids) when live data
   * is unavailable so the dashboard still renders offline.
   */
  seedFromLiveData: (customers: readonly Customer[], products: readonly Product[]) => void;
  submit: (input: SubmitReferralInput) => Referral;
  /** pending → reviewed (the actor is taking ownership to start outreach). */
  approve: (id: string) => void;
  markReviewed: (id: string) => void;
  markCampaignSent: (id: string) => void;
  /** Clear an explicit assignment back to the Branch-Manager queue. */
  release: (id: string, by: string) => void;
  reassign: (id: string, assignee: ReferralAssignee, by: string, note?: string) => void;
  deny: (id: string, reason: string, by: string) => void;
  reopen: (id: string) => void;
  clearUnread: (id: string) => void;
  /**
   * Merge API-sourced referrals (SCRUM-343): server rows take precedence and
   * prepend; existing rows with colliding ids are replaced; client seeds stay
   * so the demo remains rich before any server rows exist.
   */
  mergeApiReferrals: (rows: Referral[]) => void;
}

/**
 * SCRUM-348: workflow actions persist server-side via the workflow endpoints,
 * fire-and-forget — local state mutates immediately (instant UX, offline
 * demo keeps working); the server is the durable record once deployed.
 */
function persist(
  action: Parameters<typeof postReferralAction>[1],
  id: string,
  body?: Record<string, unknown>,
): void {
  postReferralAction(id, action, body).catch(() => {
    // API unreachable (offline preview) — local state stands alone.
  });
}

function updateReferral(
  referrals: Referral[],
  id: string,
  fn: (r: Referral) => Referral,
): Referral[] {
  return referrals.map((r) => (r.id === id ? fn(r) : r));
}

/**
 * Chooses a seed-referral customer that the referred product plausibly fits
 * (SCRUM-843): they must not already hold it, and for deposit/retirement products
 * they must actually be funded. Returns undefined when nothing matches so the
 * caller can fall back rather than seed an empty referral.
 */
function pickPlausibleCustomer(
  customers: readonly Customer[],
  productName: string,
  taken: ReadonlySet<string>,
): Customer | undefined {
  const isBusiness = /business/i.test(productName);
  /** Holding code the product would duplicate, if any. */
  const alreadyHeld = /IRA/i.test(productName)
    ? 'IRA'
    : /money market/i.test(productName)
      ? 'MM'
      : /savings/i.test(productName)
        ? 'SAV'
        : /checking/i.test(productName)
          ? 'CHK'
          : null;
  /** Deposit/retirement products need funds behind them to be a credible referral. */
  const needsFunding = /IRA|money market|savings/i.test(productName);

  // `taken` keeps the five seeded referrals on five DISTINCT customers — without it
  // every product would match the same first-eligible customer.
  return customers.find(
    (c) =>
      !taken.has(c.id) &&
      (isBusiness ? c.accountType === 'Business' : c.accountType === 'Personal') &&
      (alreadyHeld === null || !c.holdings.includes(alreadyHeld)) &&
      (!needsFunding || c.balance > 0),
  );
}

export const useReferralsStore = create<ReferralsState>((set, get) => ({
  referrals: [],
  seeded: false,

  seedFromLiveData: (customers, products) => {
    if (get().seeded) return;
    const claimed = new Set<string>();
    const referrals: Referral[] = SEED_TEMPLATE.map((t, i) => {
      const product = products.find((p) => p.name === t.productName);
      // SCRUM-843: pick a customer the referral actually makes sense for, instead of
      // whoever happened to sit at a fixed array index. Positional attachment is why a
      // referral could name a product unrelated to the customer's profile. Falls back to
      // the original index so the demo still seeds when no plausible match exists.
      const customer =
        pickPlausibleCustomer(customers, t.productName, claimed) ??
        customers[SEED_CUSTOMER_INDICES[i] ?? i];
      if (customer) claimed.add(customer.id);
      return {
        id: `ref_00${String(i + 1)}`,
        customerId: customer?.id ?? '',
        customerName: customer?.name ?? SEED_FALLBACK_NAMES[i] ?? 'Unknown customer',
        productId: product?.id ?? '',
        productName: t.productName,
        tellerName: t.tellerName,
        branch: customer?.branch ?? 'Main',
        note: t.note,
        submittedDaysAgo: t.submittedDaysAgo,
        status: t.status,
        assignee: t.assignee,
        events: [],
        tellerUnread: false,
        converted: t.converted,
      };
    });
    set({ referrals, seeded: true });
  },

  submit: (input) => {
    const referral: Referral = {
      id: `ref_${String(Date.now())}`,
      customerId: input.customerId,
      customerName: input.customerName,
      productId: input.productId,
      productName: input.productName,
      tellerName: input.submittedBy,
      branch: input.branch,
      note: input.note.trim(),
      submittedDaysAgo: 0,
      status: 'pending',
      assignee: input.assignee,
      events: [],
      tellerUnread: false,
      converted: false,
    };
    set((s) => ({ referrals: [referral, ...s.referrals] }));
    return referral;
  },

  approve: (id) => {
    persist('approve', id);
    set((s) => ({
      referrals: updateReferral(s.referrals, id, (r) =>
        r.status === 'pending' ? { ...r, status: 'reviewed' } : r,
      ),
    }));
  },

  markReviewed: (id) => {
    persist('review', id);
    set((s) => ({
      referrals: updateReferral(s.referrals, id, (r) => ({ ...r, status: 'reviewed' })),
    }));
  },

  markCampaignSent: (id) => {
    persist('campaign-sent', id);
    set((s) => ({
      referrals: updateReferral(s.referrals, id, (r) => ({ ...r, status: 'campaign_sent' })),
    }));
  },

  release: (id, by) => {
    persist('release', id);
    set((s) => ({
      referrals: updateReferral(s.referrals, id, (r) => ({
        ...r,
        assignee: null,
        tellerUnread: true,
        events: [
          ...r.events,
          { type: 'released', to: defaultOwnerFor(r.branch).name, by, daysAgo: 0 },
        ],
      })),
    }));
  },

  reassign: (id, assignee, by, note) => {
    if (assignee.userId) {
      persist('assign', id, { receivingUserId: assignee.userId, note: note ?? null });
    }
    set((s) => ({
      referrals: updateReferral(s.referrals, id, (r) => ({
        ...r,
        assignee,
        tellerUnread: true,
        events: [
          ...r.events,
          {
            type: 'reassigned' as const,
            to: assignee.name,
            by,
            daysAgo: 0,
            ...(note !== undefined && note !== '' ? { note } : {}),
          },
        ],
      })),
    }));
  },

  deny: (id, reason, by) => {
    persist('deny', id, { reason });
    set((s) => ({
      referrals: updateReferral(s.referrals, id, (r) => ({
        ...r,
        status: 'denied',
        denialNote: reason,
        tellerUnread: true,
        events: [...r.events, { type: 'denied', by, note: reason, daysAgo: 0 }],
      })),
    }));
  },

  reopen: (id) => {
    persist('reopen', id);
    set((s) => ({
      referrals: updateReferral(s.referrals, id, (r) => ({ ...r, status: 'pending' })),
    }));
  },

  clearUnread: (id) => {
    persist('read', id);
    set((s) => ({
      referrals: updateReferral(s.referrals, id, (r) => ({ ...r, tellerUnread: false })),
    }));
  },

  mergeApiReferrals: (rows) => {
    if (rows.length === 0) return;
    set((s) => {
      const apiIds = new Set(rows.map((r) => r.id));
      return { referrals: [...rows, ...s.referrals.filter((r) => !apiIds.has(r.id))] };
    });
  },
}));

/**
 * Nav-badge counts (v6 `updateReferralsNavDot`): personally-assigned open
 * referrals take precedence over the general new-referral count.
 */
export function navBadgeCounts(
  referrals: readonly Referral[],
  currentEmail: string | undefined,
): { mineCount: number; pendingCount: number } {
  const open = referrals.filter((r) => r.status === 'pending' || r.status === 'reviewed');
  return {
    mineCount: open.filter((r) => isMine(r, currentEmail)).length,
    pendingCount: referrals.filter((r) => r.status === 'pending').length,
  };
}
